import { HStack, Text, VStack, Box } from '@chakra-ui/react';
import { SearchIcon, VisibilityIcon } from 'assets/icons/v2';
import { NovaIcon } from 'components/ui/novaIcon';
import { Filters } from 'components/common/Table/Filters';
import { InputOption } from 'components/common/Table/FilterOptions/InputOption';
import { useCallback, useEffect, useState, useRef } from 'react';
import { SetURLSearchParams, useNavigate } from 'react-router-dom';
import { useGetWorksheetStudents } from 'hooks/api/backend/worksheets/useWorksheets';
import { WorksheetStudentsFilter } from 'types/Worksheets';
import { createColumns } from 'utils/tableHelper';
import { TextCellProps } from 'components/common/Table/types/Cell';
import { ClassListCell, TextCell } from 'components/common/Table/Cells';
import { WorksheetStudentData } from 'components/common/Table/types/Column';
import { Pagination } from 'components/common/Table/Pagination';
import { Table } from 'components/common/Table/Table';
import { UsageCell } from 'components/common/Table/Cells/UsageCell';
import { useModalService } from 'hooks/useModalService';
import { ModalVariant } from 'types/Modal';
import { useUser } from 'utils/auth';
import { NoSearchResult } from 'components/common/NoSearchResult';
import { QueryErrorState } from 'components/common/QueryErrorState';
import { ProtectedRoutes } from 'types/Routes';
import { NoTableContent } from 'components/common/NoTableContent';

const worksheetUserColumns = createColumns<WorksheetStudentData>()({
  firstName: {
    header: 'Vorname',
    element: (props: TextCellProps) => <TextCell w="120px" label={props.label} lineClamp={3} />,
  },
  lastName: {
    header: 'Nachname',
    element: (props: TextCellProps) => <TextCell w="140px" label={props.label} lineClamp={3} />,
  },
  email: {
    header: 'Email',
    element: (props: TextCellProps) => (
      <TextCell
        w="240px"
        label={props.label}
        textStyle="body.s.medium"
        lineClamp={3}
        textColor="grey.tertiary"
      />
    ),
  },
  worksheetCount: {
    header: 'Bearbeitete Arbeitsblätter',
    element: UsageCell,
    getCellProps: (row: WorksheetStudentData) => ({
      allowedUsage: row.assignedWorksheetCount.toString(),
      usedUsage: row.worksheetCount.toString(),
    }),
    sortDisabled: true,
  },
  memberOfClasses: {
    header: 'In Kursen',
    element: ClassListCell,
    getCellProps: (row: WorksheetStudentData) => ({
      classes: row.memberOfClasses || [],
    }),
  },
  actionItem: {
    header: 'Aktion',
    sortDisabled: true,
  },
});

interface GuestStudentsTabProps {
  searchParams: URLSearchParams;
  setSearchParams: SetURLSearchParams;
}

export const GuestStudentsTab = ({
  searchParams,
  setSearchParams,
}: GuestStudentsTabProps): JSX.Element => {
  const navigate = useNavigate();
  // Initialize from URL params
  const filterFromUrl = searchParams.get('f') as WorksheetStudentsFilter;
  const pageFromUrl = searchParams.get('p');

  const { data: user } = useUser();
  const modalService = useModalService();
  const [searchQuery, setSearchQuery] = useState<string>(searchParams.get('s') || '');
  const [activeFilter, setActiveFilter] = useState<WorksheetStudentsFilter>(
    Object.values(WorksheetStudentsFilter).includes(filterFromUrl)
      ? filterFromUrl
      : WorksheetStudentsFilter.ALL
  );
  const [currentPage, setCurrentPage] = useState<number>(
    pageFromUrl ? parseInt(pageFromUrl, 10) : 1
  );
  const pageSize = 10;

  const searchTimeoutRef = useRef<NodeJS.Timeout>();

  const {
    data: studentsData,
    isLoading,
    isError,
    error,
    refetch,
  } = useGetWorksheetStudents({
    page: currentPage,
    pageSize,
    filter: activeFilter,
    searchQuery,
  });

  const totalPages = studentsData?.totalPages || 1;

  const handleSearchChange = useCallback((value: string) => {
    setSearchQuery(value);
    setCurrentPage(1);
  }, []);

  // Sync filters
  useEffect(() => {
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }
    searchTimeoutRef.current = setTimeout(() => {
      const newParams = new URLSearchParams(searchParams);

      if (searchQuery) {
        newParams.set('s', searchQuery);
      } else {
        newParams.delete('s');
      }

      if (activeFilter !== WorksheetStudentsFilter.ALL) {
        newParams.set('f', activeFilter);
      } else {
        newParams.delete('f');
      }

      if (currentPage > 1) {
        newParams.set('p', currentPage.toString());
      } else {
        newParams.delete('p');
      }

      setSearchParams(newParams, { replace: true });
    }, 300);

    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchQuery, activeFilter, currentPage]);

  const handleFilterChange = (filter: WorksheetStudentsFilter): void => {
    setActiveFilter(filter);
    setCurrentPage(1);
  };

  const mappedStudents: WorksheetStudentData[] = (studentsData?.data ?? []).map((student) => ({
    ...student,
    actionItem: (
      <NovaIcon
        icon={VisibilityIcon}
        size="m"
        css={{ cursor: 'pointer' }}
        onClick={() => {
          modalService.show(ModalVariant.USER_WORKSHEETS, {
            classId: undefined,
            studentId: student.id,
          });
        }}
        aria-label="Antworten anzeigen"
      />
    ),
  }));

  const clearInput = (): void => setSearchQuery('');

  const filterLabels: Record<WorksheetStudentsFilter, string> = {
    [WorksheetStudentsFilter.ALL]: 'Alle',
    [WorksheetStudentsFilter.STUDENTS]: 'Auszubildende',
    [WorksheetStudentsFilter.GUESTS]: 'Gäste',
  };

  const onPageChange = (page: number): void => setCurrentPage(page);

  const hasData = !!(mappedStudents && mappedStudents.length > 0);
  const isFilterActive = activeFilter !== WorksheetStudentsFilter.ALL;
  const hasSearchQuery = !!searchQuery;
  const showNoStudentsState = !isLoading && !hasData && !isFilterActive && !hasSearchQuery;
  const showNoResultsState = !isLoading && !hasData && (isFilterActive || hasSearchQuery);

  if (isError) {
    return <QueryErrorState error={error} onRetry={() => refetch()} />;
  }

  const getListContent = (): JSX.Element => {
    if (showNoStudentsState) {
      return (
        <NoTableContent
          title="Es liegen noch keine Antworten von Auszubildenden vor."
          description="Sobald ein Arbeitsblatt beantwortet wurde, findest du die Auszubildenden hier."
          onClick={() => navigate(`${ProtectedRoutes.WORKSHEET_DASHBOARD}?t=worksheets`)}
          buttonText="Zu meinen Arbeitsblättern"
        />
      );
    }
    if (showNoResultsState) {
      return <NoSearchResult query={searchQuery} />;
    }

    return (
      <>
        <Table columns={worksheetUserColumns} data={mappedStudents} isDataLoading={isLoading} />
        {totalPages > 1 && (
          <Pagination
            totalPages={totalPages}
            currentPage={currentPage}
            onPageChange={onPageChange}
          />
        )}
      </>
    );
  };

  const showFilters = !showNoStudentsState;

  return (
    <VStack gap="l" align="stretch">
      <HStack justifyContent="space-between" alignItems="center">
        <Text textStyle="h3.medium" color="text.primary">
          Lernende
        </Text>
      </HStack>

      {showFilters && (
        <>
          <Filters
            filters={[
              [
                {
                  id: 'searchQuery',
                  element: InputOption,
                  placeholder: 'Vorname, Nachname oder E-Mail suchen',
                  value: searchQuery,
                  options: [],
                  onChange: handleSearchChange,
                  icon: <NovaIcon icon={SearchIcon} size="m" aria-hidden />,
                  onClear: clearInput,
                },
              ],
            ]}
          />
          <HStack gap="15px" px="0">
            {Object.values(WorksheetStudentsFilter).map((filter) => (
              <Box
                key={filter}
                as="button"
                bg={activeFilter === filter ? 'tag.background.green' : 'transparent'}
                border="default"
                borderColor={activeFilter === filter ? 'border.accent.active' : 'border.accent'}
                px="10px"
                py="5px"
                borderRadius="5px"
                cursor="pointer"
                _hover={{ opacity: '80' }}
                onClick={() => handleFilterChange(filter)}
              >
                <Text
                  textStyle="body.s.regular"
                  color={activeFilter === filter ? 'text.primary' : 'text.secondary'}
                  lineHeight="1.3"
                >
                  {`${filterLabels[filter]} (${studentsData?.filterCounts?.[filter] ?? 0})`}
                </Text>
              </Box>
            ))}
          </HStack>
        </>
      )}
      {getListContent()}
    </VStack>
  );
};
